import React, { useState } from "react";
import * as Icons from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, SectionCard, ProvenanceBadge, Mono, InfoNote, copyText } from "@/components/uikit";
import { PAYLOAD_TYPES, DEFAULT_ORIGIN, DEFAULT_FRAMEWORK, HASH_MODES } from "@/lib/constants";
import { LAYERS } from "@/lib/layers";
import { downloadJson } from "@/lib/download";
import { toast } from "sonner";

const PIPELINE = ["Capture", "Canonicalize", "Hash", "Anchor", "Analyze Drift", "Verify", "Reconstruct", "Export"];

export default function AnchorPage() {
  const { anchorEvent } = useApp();
  const [payload, setPayload] = useState('{\n  "event": "capture-demo",\n  "value": 128,\n  "note": "operator capture"\n}');
  const [type, setType] = useState("Digital Event");
  const [layer, setLayer] = useState("1");
  const [notes, setNotes] = useState("");
  const [mode, setMode] = useState("canonical");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!payload.trim()) {
      toast.error("Payload required");
      return;
    }
    setBusy(true);
    try {
      const rec = await anchorEvent({ payload, payload_type: type, layer: Number(layer), notes, hash_mode: mode, source_tag: "local" });
      setResult(rec);
      toast.success("Event anchored", { description: rec.event_id });
    } finally {
      setBusy(false);
    }
  };

  const download = () => {
    downloadJson(result, `signallink-receipt-${result.event_id}.json`);
    toast.success("Receipt downloaded");
  };

  return (
    <div>
      <PageHeader overline="K-Crypto Workflow" title="Anchor Event" icon="Anchor"
        subtitle="Produce a locally verifiable SHA-256 evidence record and append it to the local chain." />

      <div className="mb-6 flex flex-wrap items-center gap-1.5">
        {PIPELINE.map((step, i) => (
          <React.Fragment key={step}>
            <span className={`rounded border px-2 py-1 font-mono text-[10px] uppercase tracking-wider ${i <= 3 ? "border-cyan-700 bg-cyan-950/60 text-cyan-300" : "border-slate-800 bg-slate-950 text-slate-500"}`}>{step}</span>
            {i < PIPELINE.length - 1 && <Icons.ChevronRight className="h-3 w-3 text-slate-700" aria-hidden="true" />}
          </React.Fragment>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <SectionCard title="Capture Payload" icon="FileInput" testId="anchor-capture-card">
          <div className="space-y-4">
            <div>
              <Label htmlFor="ap-payload" className="mb-1.5 block text-slate-300">Payload</Label>
              <Textarea id="ap-payload" data-testid="anchorpage-payload-input" rows={8} value={payload} onChange={(e) => setPayload(e.target.value)}
                className="border-slate-700 bg-slate-950 font-mono text-xs text-cyan-200" />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label className="mb-1.5 block text-slate-300">Payload type</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger data-testid="anchorpage-type-select" className="border-slate-700 bg-slate-950 text-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-slate-700 bg-slate-900 text-slate-200">
                    {PAYLOAD_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block text-slate-300">ADA-4WM layer</Label>
                <Select value={layer} onValueChange={setLayer}>
                  <SelectTrigger data-testid="anchorpage-layer-select" className="border-slate-700 bg-slate-950 text-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64 border-slate-700 bg-slate-900 text-slate-200">
                    {LAYERS.map((l) => <SelectItem key={l.n} value={String(l.n)}>L{l.n} — {l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="ap-notes" className="mb-1.5 block text-slate-300">Operator notes</Label>
              <Input id="ap-notes" data-testid="anchorpage-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} className="border-slate-700 bg-slate-950 text-slate-200" />
            </div>
            <div>
              <Label className="mb-1.5 block text-slate-300">Hash mode</Label>
              <div className="flex gap-1" role="group" aria-label="Hash mode">
                {HASH_MODES.map((m) => (
                  <button key={m.value} type="button" onClick={() => setMode(m.value)} data-testid={`anchorpage-mode-${m.value}`}
                    className={`rounded border px-3 py-2 font-mono text-[11px] uppercase tracking-wider transition-colors ${
                      mode === m.value ? "border-cyan-500 bg-slate-900 text-cyan-300" : "border-slate-700 text-slate-400 hover:text-slate-200"
                    }`}>
                    {m.label}
                  </button>
                ))}
              </div>
              <p className="mt-1 text-[11px] text-slate-500">{HASH_MODES.find((m) => m.value === mode)?.detail}</p>
            </div>
            <div className="flex flex-wrap gap-2 text-xs text-slate-500">
              <span>origin: <Mono>{DEFAULT_ORIGIN}</Mono></span>
            </div>
            <Button onClick={submit} disabled={busy} className="w-full bg-cyan-500 font-bold text-slate-950 hover:bg-cyan-400 glow-cyan" data-testid="anchorpage-submit-button">
              {busy ? <Icons.Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Icons.Anchor className="mr-2 h-4 w-4" />} Anchor Event
            </Button>
          </div>
        </SectionCard>

        <SectionCard title="Evidence Receipt" icon="ReceiptText" testId="anchor-receipt-card"
          right={result && <ProvenanceBadge source="local" testId="receipt-provenance" />}>
          {!result ? (
            <div className="flex h-full min-h-[280px] flex-col items-center justify-center text-center">
              <Icons.ReceiptText className="mb-3 h-10 w-10 text-slate-700" aria-hidden="true" />
              <p className="text-sm text-slate-500">Anchor an event to generate a signed local evidence receipt.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="receipt-output">
              {[
                ["event_id", result.event_id],
                ["timestamp", result.timestamp],
                ["payload_type", result.payload_type],
                ["hash_mode", (result.hash_mode || "canonical") === "raw" ? "RAW TEXT" : "CANONICAL JSON"],
                ["framework", DEFAULT_FRAMEWORK],
                ["layer", `L${result.layer}`],
                ["previous_hash", result.previous_hash],
              ].map(([k, v]) => (
                <Row key={k} label={k} value={v} />
              ))}
              <Row label="sha256" value={result.sha256} copyable />
              <div>
                <p className="mb-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">canonical_payload</p>
                <pre className="max-h-40 overflow-auto rounded border border-slate-800 bg-slate-950 p-2 font-mono text-[11px] text-cyan-200">{result.canonical_payload}</pre>
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button onClick={download} variant="outline" className="border-slate-700 text-slate-200" data-testid="receipt-download-button">
                  <Icons.Download className="mr-2 h-4 w-4" /> Download JSON
                </Button>
                <Button onClick={() => copyText(JSON.stringify(result, null, 2), "Receipt copied")} variant="outline" className="border-slate-700 text-slate-200" data-testid="receipt-copy-button">
                  <Icons.Copy className="mr-2 h-4 w-4" /> Copy receipt
                </Button>
              </div>
              <InfoNote tone="info">Verification method: {result.verification_method}. This is LOCAL evidence only.</InfoNote>
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}

function Row({ label, value, copyable }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded border border-slate-800 bg-slate-950/60 px-3 py-1.5">
      <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
      <span className="flex items-center gap-1.5">
        <Mono className="max-w-[200px] truncate sm:max-w-[300px]">{value}</Mono>
        {copyable && (
          <button type="button" aria-label={`Copy ${label}`} className="text-slate-500 hover:text-cyan-400"
            onClick={() => copyText(value, "Copied hash")}>
            <Icons.Copy className="h-3.5 w-3.5" />
          </button>
        )}
      </span>
    </div>
  );
}
